import React from 'react';

import AddDatasetToAnalysisConfigurationModal, {
  AddDatasetToAnalysisConfiguration,
} from 'analysis-tool/AddDatasetToAnalysisConfigurationModal';
import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import {TableIndicatorStatus} from 'notebook/tables/errors/TableErrorConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import getPlainTextTable from 'notebook/tables/export/getPlainTextTable';
import EntryStore from 'stores/EntryStore';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import APIUtil from 'util/APIUtil';

import {
  addDatasetToExistingAnalysis,
  addDatasetToNewAnalysis,
  AddDatasetToExistingAnalysisResponse,
  AddDatasetToNewAnalysisResponse,
} from '../ActionFunctionDAO';
import toColumnLabel from '../columns/toColumnLabel';
import {
  LiveRowMetadata,
  StructuredTable,
  StructuredTableAdapter,
  TableActionFunctionParameters,
} from '../StructuredTableConstants';
import {ActionFunctionContext} from './ActionFunctionContext';
import makeCustomButtonActionFunction from './makeCustomButtonActionFunction';

export const CREATE_DATASET_BUTTON_TITLE = 'Add to analysis';

export const NEW_ANALYSIS_OPTION = 'Add to new analysis';
export const EXISTING_ANALYSIS_OPTION = 'Add to existing analysis';

export interface CreateDatasetFromNotebookParams<
  TTableMetadata,
  TRowMetadata,
> extends TableActionFunctionParameters<TTableMetadata, TRowMetadata> {}

interface CreateDatasetButtonProps {
  disabled: boolean;
  handleClickActionButton: (actionType: ActionType) => void;
}

export enum ActionType {
  NEW_ANALYSIS = 'NEW_ANALYSIS',
  EXISTING_ANALYSIS = 'EXISTING_ANALYSIS',
}

export function structuredTableToPlainTextTable<TTableMetadata, TRowMetadata>(args: {
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  structuredTableAdapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
  computedTable: Array<Array<EvaluatedCell>>;
  liveRowMetadataByRow?: Array<LiveRowMetadata>;
}): Promise<Array<Array<string>>> {
  const plainTextTable = getPlainTextTable({
    table: args.structuredTableAdapter.toTable(args.structuredTable).table,
    computedTable: args.computedTable,
    includeCellValueModifiers: false,
    headerRow: args.structuredTable.columns.map(
      (structuredTableColumn) =>
        toColumnLabel({structuredTableColumn, schemas: args.structuredTable.schemas}) || 'unknown'
    ),
    useIds: true,
  }).filter((_, index) => {
    // Should always include header row (index 0)
    if (index === 0 || args.liveRowMetadataByRow == null || args.liveRowMetadataByRow[index - 1] == null) {
      return true;
    }
    return args.liveRowMetadataByRow[index - 1].archivePurpose == null;
  });
  return Promise.resolve(plainTextTable);
}

export async function handleClickActionButton<TTableMetadata, TRowMetadata>(
  props: {
    entryOrTemplateId: string | null;
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
    structuredTableAdapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
    computedTable: Array<Array<EvaluatedCell>>;
    liveRowMetadataByRow?: Array<LiveRowMetadata>;
    actionFunctionContext: ActionFunctionContext;
    structuredTableToPlainTextTable: (args: {
      structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
      structuredTableAdapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
      computedTable: Array<Array<EvaluatedCell>>;
      liveRowMetadataByRow?: Array<LiveRowMetadata>;
    }) => Promise<Array<Array<string>>>;
  },
  actionType: ActionType
): Promise<void> {
  const {entryOrTemplateId, structuredTable, computedTable, actionFunctionContext} = props;

  const plainTextTable = await props.structuredTableToPlainTextTable(props);

  function handleAddDatasetToNewAnalysis(
    params: CreateAnalysisParams,
    dataFrameName: string
  ): Promise<AddDatasetToNewAnalysisResponse> {
    assert(entryOrTemplateId != null, `Create dataset action is only supported in entries.`);

    return addDatasetToNewAnalysis({
      entryId: entryOrTemplateId,
      folderId: params.folderId,
      structuredTable,
      evaluatedRows: computedTable,
      actionFunctionContext,
      plainTextTable,
      dataFrameName,
      analysisName: params.name,
    }).then((data: AddDatasetToNewAnalysisResponse) => {
      FileBrowserActions.openFile(data.analysisId);
      return data;
    });
  }

  function handleAddDatasetToExistingAnalysis(
    params: AddDatasetToAnalysisConfiguration
  ): Promise<AddDatasetToExistingAnalysisResponse> {
    assert(entryOrTemplateId != null, `Create dataset action is only supported in entries.`);

    return addDatasetToExistingAnalysis(
      entryOrTemplateId,
      structuredTable,
      computedTable,
      actionFunctionContext,
      plainTextTable,
      params.dataFrameName,
      params.analysisId
    ).then((data: AddDatasetToExistingAnalysisResponse) => {
      FileBrowserActions.openFile(params.analysisId);
      return data;
    });
  }

  if (actionType === ActionType.NEW_ANALYSIS) {
    // Using a dynamic import() here because otherwise we get a large import cycle between notebook and pipeline code
    import('analysis-tool/create-analysis-modal/CreateAnalysisModal').then(({CreateAnalysisModal}) =>
      legacyRunModal(CreateAnalysisModal, {
        title: 'Add to new analysis',
        onCreateAnalysis: handleAddDatasetToNewAnalysis,
        actionOrigin: ActionOrigin.NOTEBOOK,
        viaExistingTableProps: {initialDatasetName: structuredTable.name},
      })
    );
  } else {
    legacyRunModal(AddDatasetToAnalysisConfigurationModal, {
      onCreateDataFrame: handleAddDatasetToExistingAnalysis,
      initialDatasetName: structuredTable.name,
    });
  }
}

export function CreateDatasetButton(props: CreateDatasetButtonProps): JSX.Element {
  return (
    <Dropdown
      tooltip={CREATE_DATASET_BUTTON_TITLE}
      menu={
        <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
          <Option
            onSelect={() => {
              props.handleClickActionButton(ActionType.NEW_ANALYSIS);
            }}
            shouldEllipsize={true}
          >
            {NEW_ANALYSIS_OPTION}
          </Option>
          <Option
            onSelect={() => {
              props.handleClickActionButton(ActionType.EXISTING_ANALYSIS);
            }}
            shouldEllipsize={true}
          >
            {EXISTING_ANALYSIS_OPTION}
          </Option>
        </Menu>
      }
    >
      <Button
        disabled={props.disabled}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MINI}
        className="u-mini-spacing-left"
        icon={ModelTypeConstants.ANALYSIS}
        iconAltText={CREATE_DATASET_BUTTON_TITLE}
        isDropdown={true}
        dataTestAttrs={{
          component: 'CreateAnalysisButton',
          element: 'button',
        }}
      />
    </Dropdown>
  );
}

function isLatest(apiId: string): boolean | undefined {
  if (APIUtil.isDocument(apiId)) {
    return DocumentStore.getVersionState(apiId)?.isLatest;
  } else if (APIUtil.isEntry(apiId)) {
    return EntryStore.getVersionState(apiId)?.isLatest;
  }
  throw new Error(`Cannot check version status for ${apiId}`);
}

export function isButtonDisabled<TTableMetadata, TRowMetadata>(
  params: CreateDatasetFromNotebookParams<TTableMetadata, TRowMetadata>
): boolean {
  return (
    params.entryOrTemplateId == null ||
    APIUtil.isEntryOrSubOrWorkflowTemplate(params.entryOrTemplateId) ||
    params.tableStatus === TableIndicatorStatus.ERROR ||
    params.tableStatus === TableIndicatorStatus.UNSUBMITTED_CHANGES ||
    params.tableStatus === TableIndicatorStatus.TABLE_OR_SCHEMA_ARCHIVED ||
    !isLatest(params.entryOrTemplateId) ||
    params.structuredTable.rows.length === 0
  );
}

export function renderDropdownButton(props: CreateDatasetFromNotebookParams<unknown, unknown>): JSX.Element {
  return (
    <CreateDatasetButton
      disabled={isButtonDisabled(props)}
      handleClickActionButton={(actionType) => {
        handleClickActionButton({...props, structuredTableToPlainTextTable}, actionType);
      }}
    />
  );
}

/**
 * An ActionFunction that creates a dataset from the Notebook table data
 * and adds it to either a new or existing analysis.
 */
export const createDatasetActionFunction = makeCustomButtonActionFunction({
  renderToolbarButton: renderDropdownButton,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
  getHidden: (params: CreateDatasetFromNotebookParams<unknown, unknown>) =>
    !BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS ||
    APIUtil.isStageEntry(params.entryOrTemplateId ?? '') ||
    APIUtil.isStageEntryTemplate(params.entryOrTemplateId ?? ''),
});
